import {
  deleteAdminWalkInDraft,
  listAdminWalkInDrafts,
  upsertAdminWalkInDraft,
} from "../api/adminWalkInBookingApi";

const LIST_KEY = "sundorbonWalkInDrafts";
const ACTIVE_KEY = "sundorbonWalkInActiveDraftId";
const LEGACY_KEY = "sundorbonWalkInDraft";
const RETURN_PATH_KEY = "sundorbonReturnPath";
const RESTORED_TOAST_KEY = "sundorbonWalkInRestoredToast";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_DRAFTS = 40;
const deletedDraftKeys = new Set();

function rememberDeletedDraft(draft) {
  [draft?.id, draft?.clientKey, draft?.serverId].forEach((value) => {
    if (value) deletedDraftKeys.add(String(value));
  });
}

function forgetDeletedDraft(draft) {
  [draft?.id, draft?.clientKey, draft?.serverId].forEach((value) => {
    if (value) deletedDraftKeys.delete(String(value));
  });
}

function isDeletedDraft(draft) {
  return [draft?.id, draft?.clientKey, draft?.serverId].some(
    (value) => value && deletedDraftKeys.has(String(value)),
  );
}

export const WALK_IN_DRAFT_STEPS = [
  "Route",
  "Sender",
  "Receiver",
  "Accounts",
  "Parcel",
  "Delivery",
  "Payment",
  "Summary",
];

function safeParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function newDraftId() {
  return `draft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function isExpired(savedAt) {
  const at = Number(savedAt || 0);
  return Boolean(at) && Date.now() - at > TTL_MS;
}

function readStore() {
  if (typeof window === "undefined") return { items: [] };
  try {
    const parsed = safeParse(localStorage.getItem(LIST_KEY));
    const items = Array.isArray(parsed?.items)
      ? parsed.items.filter((item) => item?.id && !isExpired(item.savedAt))
      : [];
    return { items };
  } catch {
    return { items: [] };
  }
}

function writeStore(store) {
  if (typeof window === "undefined") return;
  try {
    const items = (store.items || [])
      .filter((item) => item?.id && !isExpired(item.savedAt))
      .sort((a, b) => Number(b.savedAt || 0) - Number(a.savedAt || 0))
      .slice(0, MAX_DRAFTS);
    localStorage.setItem(LIST_KEY, JSON.stringify({ items }));
  } catch {
    /* quota / private mode */
  }
}

function migrateLegacyDraft() {
  if (typeof window === "undefined") return;
  try {
    const legacy =
      safeParse(sessionStorage.getItem(LEGACY_KEY)) ||
      safeParse(localStorage.getItem(LEGACY_KEY));
    sessionStorage.removeItem(LEGACY_KEY);
    localStorage.removeItem(LEGACY_KEY);
    if (!legacy || typeof legacy !== "object") return;
    if (isExpired(legacy.savedAt)) return;
    const store = readStore();
    if (store.items.length) return;
    const id = legacy.id || newDraftId();
    store.items = [{ ...legacy, id, version: 1, savedAt: legacy.savedAt || Date.now() }];
    writeStore(store);
    setActiveWalkInDraftId(id);
  } catch {
    /* ignore */
  }
}

function isMeaningfulDraft(draft) {
  const data = draft?.data || {};
  const products = Array.isArray(data.producDetails) ? data.producDetails : [];
  return Boolean(
    data.route ||
      data.senderName ||
      data.receiverName ||
      data.senderMobileNumber ||
      data.receiverMobileNumber ||
      data.walkInFinalPrice ||
      products.some(
        (item) =>
          item?.itemDescription ||
          item?.productType ||
          item?.productGroup ||
          Number(item?.productQty) > 0,
      ),
  );
}

export function listWalkInDrafts() {
  migrateLegacyDraft();
  return readStore().items;
}

export function countWalkInDrafts() {
  return listWalkInDrafts().length;
}

export function getWalkInDraft(id) {
  if (!id) return null;
  const key = String(id);
  return (
    listWalkInDrafts().find(
      (item) =>
        item.id === key ||
        item.clientKey === key ||
        String(item.serverId || "") === key,
    ) || null
  );
}

export function getActiveWalkInDraftId() {
  if (typeof window === "undefined") return "";
  try {
    migrateLegacyDraft();
    const id = localStorage.getItem(ACTIVE_KEY) || "";
    const found = id ? getWalkInDraft(id) : null;
    if (found?.id && found.id !== id) setActiveWalkInDraftId(found.id);
    return found?.id || "";
  } catch {
    return "";
  }
}

export function setActiveWalkInDraftId(id) {
  if (typeof window === "undefined") return;
  try {
    const value = String(id || "");
    if (value) localStorage.setItem(ACTIVE_KEY, value);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch {
    /* ignore */
  }
}

export function upsertWalkInDraft(id, partial = {}) {
  const draftId = id || getActiveWalkInDraftId() || newDraftId();
  const store = readStore();
  const prev =
    store.items.find(
      (item) =>
        item.id === draftId ||
        item.clientKey === draftId ||
        String(item.serverId || "") === String(draftId),
    ) || { id: draftId };
  const next = {
    ...prev,
    ...partial,
    id: prev.id || draftId,
    clientKey: partial.clientKey || prev.clientKey || prev.id || draftId,
    version: 1,
    savedAt: Date.now(),
  };
  if (!isMeaningfulDraft(next) && !id && !getActiveWalkInDraftId()) {
    return null;
  }
  store.items = [
    next,
    ...store.items.filter(
      (item) =>
        item.id !== next.id &&
        item.id !== draftId &&
        item.clientKey !== next.clientKey,
    ),
  ];
  writeStore(store);
  setActiveWalkInDraftId(next.id);
  return next;
}

export async function deleteWalkInDraft(id) {
  if (!id) return;
  const store = readStore();
  const current =
    store.items.find(
      (item) => item.id === id || item.clientKey === id || String(item.serverId || "") === String(id),
    ) || { id };
  rememberDeletedDraft(current);
  store.items = store.items.filter(
    (item) =>
      item.id !== current.id &&
      item.id !== id &&
      item.clientKey !== id &&
      item.clientKey !== current.clientKey,
  );
  writeStore(store);
  let storedActive = "";
  try {
    storedActive = localStorage.getItem(ACTIVE_KEY) || "";
  } catch {
    storedActive = "";
  }
  if (
    storedActive === id ||
    storedActive === current.id ||
    storedActive === String(current.clientKey || "")
  ) {
    setActiveWalkInDraftId("");
  }
  try {
    await removeWalkInDraftFromServer(current);
  } catch (err) {
    forgetDeletedDraft(current);
    throw err;
  }
}

export function walkInDraftTitle(draft) {
  const data = draft?.data || {};
  const sender = String(data.senderName || "").trim();
  const receiver = String(data.receiverName || "").trim();
  if (sender && receiver) return `${sender} → ${receiver}`;
  if (sender) return sender;
  if (receiver) return `To ${receiver}`;
  return "Untitled draft";
}

export function walkInDraftStepLabel(draft) {
  const idx = Number(draft?.currentStepIndex) || 0;
  return WALK_IN_DRAFT_STEPS[idx] || WALK_IN_DRAFT_STEPS[0];
}

function serverIdOf(value) {
  const raw = value?.serverId ?? value?.id ?? value;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

export function mapServerDraft(row = {}) {
  let payload = row.payload ?? row.Payload ?? {};
  if (typeof payload === "string") {
    payload = safeParse(payload) || {};
  }
  const id = String(row.id ?? row.Id ?? payload.serverId ?? payload.id ?? "");
  const savedAt = Date.parse(
    row.modificationDate ||
      row.ModificationDate ||
      row.creationDate ||
      row.CreationDate ||
      "",
  );
  return {
    ...payload,
    id,
    serverId: Number(row.id ?? row.Id) || payload.serverId || 0,
    clientKey: row.clientKey || row.ClientKey || payload.clientKey || payload.id || "",
    savedAt: Number.isFinite(savedAt) ? savedAt : Date.now(),
    currentStepIndex:
      payload.currentStepIndex ?? row.currentStepIndex ?? row.CurrentStepIndex ?? 0,
    data: payload.data || {},
    totalCost: payload.totalCost ?? row.amount ?? row.Amount ?? 0,
  };
}

function replaceLocalDraft(previousId, next) {
  const store = readStore();
  store.items = [
    next,
    ...store.items.filter(
      (item) =>
        item.id !== previousId &&
        item.id !== next.id &&
        item.clientKey !== next.clientKey,
    ),
  ];
  writeStore(store);
  setActiveWalkInDraftId(next.id);
  return next;
}

export async function syncWalkInDraftToServer(draft) {
  if (!draft || isDeletedDraft(draft) || !isMeaningfulDraft(draft)) return null;
  const data = draft.data || {};
  const clientKey = String(draft.clientKey || draft.id || "");
  const numericId = serverIdOf(draft);
  const saved = await upsertAdminWalkInDraft({
    id: numericId,
    clientKey,
    title: walkInDraftTitle(draft),
    senderName: data.senderName || "",
    receiverName: data.receiverName || "",
    senderMobile: data.senderMobileNumber || "",
    receiverMobile: data.receiverMobileNumber || "",
    currentStepIndex: Number(draft.currentStepIndex) || 0,
    amount: parseFloat(data.walkInFinalPrice || draft.totalCost || 0) || 0,
    payload: JSON.stringify({
      ...draft,
      clientKey,
      pendingSubmit: false,
    }),
  });
  const mapped = mapServerDraft(saved);
  return replaceLocalDraft(draft.id, mapped);
}

export async function refreshWalkInDraftsFromServer() {
  const rows = await listAdminWalkInDrafts();
  const items = rows
    .map(mapServerDraft)
    .filter((item) => item.id && !isDeletedDraft(item));
  writeStore({ items });
  let active = "";
  try {
    active = localStorage.getItem(ACTIVE_KEY) || "";
  } catch {
    active = "";
  }
  if (active) {
    const match = items.find(
      (item) =>
        item.id === active ||
        item.clientKey === active ||
        String(item.serverId || "") === String(active),
    );
    setActiveWalkInDraftId(match?.id || "");
  }
  return items;
}

export async function removeWalkInDraftFromServer(idOrDraft) {
  const draft =
    idOrDraft && typeof idOrDraft === "object"
      ? idOrDraft
      : getWalkInDraft(idOrDraft) || { id: idOrDraft };
  const numericId = serverIdOf(draft);
  await deleteAdminWalkInDraft(numericId, draft.clientKey || draft.id);
}

/** Active draft for the open wizard. */
export function readWalkInDraft() {
  migrateLegacyDraft();
  const id = getActiveWalkInDraftId();
  return id ? getWalkInDraft(id) : null;
}

export function saveWalkInDraft(partial = {}) {
  return upsertWalkInDraft(getActiveWalkInDraftId(), partial);
}

export function clearWalkInDraft() {
  const id = getActiveWalkInDraftId();
  if (id) void deleteWalkInDraft(id);
  else setActiveWalkInDraftId("");
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(RESTORED_TOAST_KEY);
  } catch {
    /* ignore */
  }
}

export function markWalkInDraftRestoredToast() {
  if (typeof window === "undefined") return false;
  try {
    if (sessionStorage.getItem(RESTORED_TOAST_KEY) === "1") return false;
    sessionStorage.setItem(RESTORED_TOAST_KEY, "1");
    return true;
  } catch {
    return true;
  }
}

export function rememberAuthReturnPath(path) {
  if (typeof window === "undefined") return;
  try {
    const value =
      path ||
      `${window.location.pathname}${window.location.search || ""}`;
    sessionStorage.setItem(RETURN_PATH_KEY, value);
  } catch {
    /* ignore */
  }
}

export function consumeAuthReturnPath(fallback = "/admin-dashboard/walk-in-booking") {
  if (typeof window === "undefined") return fallback;
  try {
    const value = sessionStorage.getItem(RETURN_PATH_KEY);
    sessionStorage.removeItem(RETURN_PATH_KEY);
    if (!value || value.startsWith("/auth/silent-callback")) return fallback;
    return value;
  } catch {
    return fallback;
  }
}
